<template>
  <!-- Default (legacy) variant preserved -->
  <form v-if="variant === 'default'" class="flex">
    <label for="search_field" class="sr-only">Search</label>
    <div class="flex items-center w-56 px-4">
      <div class="inset-y-0 left-0 -mr-8 flex items-center z-10">
        <svg
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          class="block w-6 h-6 text-navy"
          v-if="(form.search && form.search.length == 0) || !form.search"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <svg
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          class="block w-6 h-6 text-navy cursor-pointer"
          v-if="form.search && form.search.length > 0"
          @click="form.search=''; reset();"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <input
        id="search"
        name="search"
        placeholder="Search"
        type="text"
        v-model="form.search"
        @keyup="searching"
        class="block w-full h-full pl-10 pr-3 py-2 border-0 border-b border-navy bg-transparent text-navy-dark placeholder-navy-dark placeholder-opacity-70 focus:outline-none focus:ring-0 focus:border-transparent text-sm font-bold"
      />
      <div class="flex items-end h-full" title="Full word" v-if="showFullWordToggle">
        <a href="#" @click.prevent="toggleFullWord">
          <svg-icon
            type="mdi"
            size="18"
            :path="icons.mdiFormatLetterMatches"
            class="text-navy"
          />
        </a>
      </div>
    </div>
  </form>

  <!-- Toolbar variant: button-like styling to match header controls -->
  <form v-else-if="variant === 'toolbar'" @submit.prevent class="flex">
    <label for="search_toolbar" class="sr-only">Search</label>
    <div :class="['relative inline-flex items-center h-8 rounded-md border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-navy-light transition-colors pl-2 pr-1', widthClass]">
      <!-- leading search icon -->
      <svg
        v-if="!form.search || form.search.length === 0"
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4 text-gray-500 mr-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <!-- input -->
      <input
        id="search_toolbar"
        type="text"
        autocomplete="off"
        :placeholder="placeholder"
        v-model="form.search"
        @keyup="searching"
        class="bg-transparent border-0 outline-none focus:outline-none focus:ring-0 text-xs placeholder-gray-400 text-gray-700 w-full pr-4"
      />
      <!-- clear button -->
      <button
        v-if="form.search && form.search.length > 0"
        type="button"
        @click="form.search=''; reset();"
        aria-label="Clear search"
        class="ml-1 h-5 w-5 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm2.28-10.53a.75.75 0 10-1.06-1.06L10 7.94 8.78 6.72a.75.75 0 10-1.06 1.06L8.94 9l-1.22 1.22a.75.75 0 101.06 1.06L10 10.06l1.22 1.22a.75.75 0 101.06-1.06L11.06 9l1.22-1.22z" clip-rule="evenodd"/></svg>
      </button>
      <!-- optional full-word toggle -->
      <button
        v-if="showFullWordToggle"
        type="button"
        @click.prevent="toggleFullWord"
        class="ml-1 h-5 w-5 inline-flex items-center justify-center rounded text-gray-500 hover:text-navy hover:bg-gray-100 focus:outline-none"
        :title="fullWordTitle"
      >
        <svg-icon type="mdi" size="16" :path="icons.mdiFormatLetterMatches" />
      </button>
    </div>
  </form>
</template>

<script>
import _ from "lodash";
import { mdiFormatLetterMatches } from "@mdi/js";
export default {
  props: {
    form: Object,
    url: String,
    variant: { type: String, default: 'default' },
    placeholder: { type: String, default: 'Search' },
    showFullWordToggle: { type: Boolean, default: true },
  width: { type: String, default: 'default' } // default | narrow | wide | xl
  },
  data() {
    return {
      icons: {
        mdiFormatLetterMatches,
      },
    };
  },
  watch: {
    "form.search": function () {
      this.searching();
    },
  },
  computed: {
    widthClass() {
      switch(this.width) {
        case 'narrow': return 'w-44';
        case 'wide': return 'w-72';
        case 'xl': return 'w-80';
        case 'full': return 'w-full';
        default: return 'w-56';
      }
    },
    fullWordTitle() {
      return this.form.search && this.form.search.startsWith('"') ? 'Disable full word match' : 'Enable full word match';
    }
  },
  methods: {
    searching: _.debounce(function () {
      this.$inertia.get(this.url, this.form);
      this.$emit("searching");
    }, 750),
    reset() {
      this.form.search = "";

      this.$emit("reset");
    },
    toggleFullWord() {
      if (this.form.search.startsWith('"')) {
        this.form.search = this.form.search.slice(1, -1);
      } else {
        this.form.search = `"${this.form.search}`;
      }
    },
  },
};
</script>
